import { createFileRoute, Link } from "@tanstack/react-router"
import type { Id } from "../../convex/_generated/dataModel"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { ApplicantProfileDialog } from "@/components/applicant-profile-dialog"
import { ApplicantListRow } from "@/components/recruit/applicant-list-row"
import { ApplicantReviewPanel } from "@/components/recruit/applicant-review-panel"
import { EditJobDialog } from "@/components/recruit/edit-job-dialog"
import { useJobDetail, type StatusFilter } from "@/hooks/use-job-detail"
import { StatusPill } from "@/components/status-pill"
import { EmptyState } from "@/components/empty-state"
import { TagChips } from "@/components/chips"
import { cn } from "@/lib/utils"
import { errorMessage } from "@/lib/errors"
import { jobWorkModeLabel } from "@/lib/employment"
import { formatSalary } from "@/lib/salary"
import { toast } from "sonner"
import { useState } from "react"
import { useCompanyCapabilities } from "@/lib/company-capabilities"
import { ShareJobButton } from "@/features/jobs"
import { PaywallDialog } from "@/components/paywall-dialog"
import { JOB_FEATURE_PAISE } from "../../convex/lib/constants"

export const Route = createFileRoute("/recruit/jobs_/$jobId")({
  component: EmployerJobDetail,
})

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "applied", label: "Applied" },
  { value: "reviewed", label: "Reviewed" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "rejected", label: "Not selected" },
]

function EmployerJobDetail() {
  const { jobId } = Route.useParams()
  const capabilities = useCompanyCapabilities()
  const canManageJob = capabilities?.manageJobLifecycle === true
  const {
    job,
    applicants,
    close,
    reopen,
    viewedApplicant,
    setViewedApplicant,
    selectedApplicant,
    selectedId,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    statusCounts,
    filteredApplicants,
    pendingCount,
    setApplicantStatus,
    pickApplicant,
  } = useJobDetail(jobId as Id<"jobs">)
  const [editOpen, setEditOpen] = useState(false)
  const [featureOpen, setFeatureOpen] = useState(false)

  if (job === undefined) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (!job) {
    return (
      <div>
        <p>Job not found.</p>
        <Link to="/recruit/jobs" className="home-wf-btn home-wf-btn--outline mt-4 inline-flex">
          Back to jobs
        </Link>
      </div>
    )
  }

  const salaryLabel = formatSalary(job)
  const featuredActive = (job.featuredUntil ?? 0) > Date.now()
  const featuredDaysLeft = featuredActive
    ? Math.max(
        1,
        Math.ceil(((job.featuredUntil ?? 0) - Date.now()) / (24 * 60 * 60 * 1000)),
      )
    : 0
  const featurePrice = `₹${(JOB_FEATURE_PAISE / 100).toLocaleString("en-IN")}`
  const hasFilters = search.trim() !== "" || statusFilter !== "all"

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/recruit/jobs"
            className="text-sm font-semibold text-[color:var(--wf-mute)] underline-offset-4 hover:text-[color:var(--wf-ink)] hover:underline"
          >
            ← Your jobs
          </Link>
          <h1 className="mkt-page-title mt-2">{job.title}</h1>
          <p className="mkt-page-sub !mt-2">
            {job.employmentType} · {job.location}
            {job.workMode
              ? ` · ${jobWorkModeLabel(job.workMode) ?? job.workMode}`
              : ""}
            {salaryLabel ? ` · ${salaryLabel}` : ""}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <StatusPill domain="job" status={job.status} />
            {featuredActive && (
              <span className="mkt-chip">
                Featured · {featuredDaysLeft} day{featuredDaysLeft === 1 ? "" : "s"} left
              </span>
            )}
            <span className="mkt-chip mkt-chip--outline">
              {applicants === undefined
                ? "… applicants"
                : (job.applicantCount ?? 0) === 0
                  ? "No applicants yet"
                  : `${job.applicantCount} applicant${job.applicantCount === 1 ? "" : "s"}`}
            </span>
            {pendingCount > 0 && (
              <span className="mkt-chip">{pendingCount} to review</span>
            )}
          </div>
        </div>
        {(job.status === "open" || canManageJob) && (
          <div className="flex flex-wrap gap-2">
            {job.status === "open" && (
              <ShareJobButton
                jobId={job._id}
                title={job.title}
                variant="outline"
              />
            )}
            {canManageJob && job.status === "open" && (
              <button
                type="button"
                className="home-wf-btn home-wf-btn--lime"
                onClick={() => setFeatureOpen(true)}
              >
                {featuredActive ? `Extend feature · ${featurePrice}` : `Feature this role · ${featurePrice}`}
              </button>
            )}
            {canManageJob && (
              <>
                <button
                  type="button"
                  className="home-wf-btn home-wf-btn--outline"
                  onClick={() => setEditOpen(true)}
                >
                  Edit posting
                </button>
                {job.status === "open" ? (
                  <button
                    type="button"
                    className="home-wf-btn home-wf-btn--outline"
                    onClick={async () => {
                      try {
                        await close({ jobId: job._id })
                        toast.success("Job closed")
                      } catch (e) {
                        toast.error(
                          errorMessage(e, "Failed to close job"),
                        )
                      }
                    }}
                  >
                    Close job
                  </button>
                ) : (
                  <button
                    type="button"
                    className="home-wf-btn home-wf-btn--outline"
                    onClick={async () => {
                      try {
                        await reopen({ jobId: job._id })
                        toast.success("Job reopened")
                      } catch (e) {
                        toast.error(
                          errorMessage(e, "Failed to reopen job"),
                        )
                      }
                    }}
                  >
                    Reopen job
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-extrabold tracking-tight text-[color:var(--wf-ink)]">
            Applicants
          </h2>
          <p className="mt-1 text-sm text-[color:var(--wf-mute)]">
            Strongest matches first — select someone to review and update status.
          </p>
        </div>

        {applicants === undefined ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full max-w-lg" />
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : applicants.length === 0 ? (
          <EmptyState
            title="No applicants yet."
            description="Share the public role link or wait for candidates from the board."
          />
        ) : (
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="min-w-0 space-y-4">
              <Input
                placeholder="Search name, headline, email, cover note…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-md rounded-full border-[rgb(12_15_20/0.12)]"
              />

              <div className="flex flex-wrap gap-2">
                {STATUS_TABS.map((tab) => {
                  const count = statusCounts[tab.value]
                  if (tab.value !== "all" && count === 0) return null
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      className={cn(
                        "home-wf-btn !px-3 !py-1 text-xs",
                        statusFilter === tab.value
                          ? "home-wf-btn--lime"
                          : "home-wf-btn--outline",
                      )}
                      onClick={() => setStatusFilter(tab.value)}
                    >
                      {tab.label}
                      <span className="ml-1 tabular-nums opacity-80">{count}</span>
                    </button>
                  )
                })}
              </div>

              <p className="text-sm text-[color:var(--wf-mute)]">
                {filteredApplicants.length === 0
                  ? "No applicants match your filters"
                  : `${filteredApplicants.length} shown · sorted by match`}
              </p>

              <div className="mt-1">
                {filteredApplicants.length === 0 ? (
                  <EmptyState
                    title="No applicants match your filters."
                    action={
                      hasFilters
                        ? {
                            label: "Clear filters",
                            onClick: () => {
                              setSearch("")
                              setStatusFilter("all")
                            },
                          }
                        : undefined
                    }
                  />
                ) : (
                  filteredApplicants.map((a) => (
                    <ApplicantListRow
                      key={a._id}
                      applicant={a}
                      selected={selectedId === a._id}
                      onSelect={() => pickApplicant(a)}
                    />
                  ))
                )}
              </div>
            </div>

            <aside className="hidden lg:block">
              {selectedApplicant ? (
                <ApplicantReviewPanel
                  applicant={selectedApplicant}
                  onOpenFull={() => setViewedApplicant(selectedApplicant)}
                  onStatus={setApplicantStatus}
                />
              ) : (
                <div className="mkt-panel mkt-panel--wash text-center">
                  <p className="mkt-panel__title">Select an applicant</p>
                  <p className="mkt-panel__copy">
                    Pick someone from the list to review their profile and update
                    status.
                  </p>
                </div>
              )}
            </aside>
          </div>
        )}
      </section>

      <details className="rounded-[0.65rem] border border-[rgb(12_15_20/0.08)] bg-[rgb(12_15_20/0.02)] px-4 py-3">
        <summary className="cursor-pointer text-sm font-bold text-[color:var(--wf-ink)]">
          Posting details
        </summary>
        <div className="mt-4 space-y-4 pb-2">
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-[color:var(--wf-ink)]/90">
            {job.description}
          </div>
          {job.skills.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <TagChips items={job.skills} />
            </div>
          )}
        </div>
      </details>

      <ApplicantProfileDialog
        open={viewedApplicant !== null}
        onOpenChange={(open) => {
          if (!open) setViewedApplicant(null)
        }}
        applicant={viewedApplicant}
      />

      {canManageJob && (
        <EditJobDialog open={editOpen} onOpenChange={setEditOpen} job={job} />
      )}
      {canManageJob && (
        <PaywallDialog
          open={featureOpen}
          onOpenChange={setFeatureOpen}
          kind="employer_job_feature"
          jobId={job._id}
          onPaid={() => setFeatureOpen(false)}
        />
      )}
    </div>
  )
}
